import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router";

import { cn } from "@/lib/cn";
import { formatNumber } from "@/lib/format";
import { ticketsApi, withSubqueueCounts, type QueueNode } from "@/lib/tickets-api";

function ancestorsOf(id: number | null, byId: Map<number, QueueNode>): Set<number> {
  const found = new Set<number>();
  for (let node = id === null ? undefined : byId.get(id); node?.parent_id != null;) {
    found.add(node.parent_id);
    node = byId.get(node.parent_id);
  }
  return found;
}

/** The queues the user can see, as a collapsible tree with ticket counts; a queue opens the filtered list. */
export function QueueTree({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation();
  const queues = useQuery({
    queryKey: ["queues"],
    queryFn: ticketsApi.queues,
    staleTime: 60_000,
    select: withSubqueueCounts,
  });
  const [params] = useSearchParams();
  const onList = useLocation().pathname === "/tickets";
  const active = onList && params.get("queue") ? Number(params.get("queue")) : null;
  const [opened, setOpened] = useState<Set<number>>(() => new Set());

  if (!queues.data) return null;
  if (queues.data.length === 0) {
    return <p className="px-3 text-[0.8rem] leading-relaxed text-ink-subtle">{t("nav.noQueues")}</p>;
  }
  const byId = new Map(queues.data.map((q) => [q.id, q]));
  const children = new Map<number | null, QueueNode[]>();
  for (const queue of queues.data) {
    // A queue whose parent is not visible is shown at the top level.
    const parent = queue.parent_id !== null && byId.has(queue.parent_id) ? queue.parent_id : null;
    children.set(parent, [...(children.get(parent) ?? []), queue]);
  }
  const expanded = new Set([...opened, ...ancestorsOf(active, byId)]);
  const toggle = (id: number) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpened(next);
  };

  const render = (parent: number | null, level: number) => (
    <ul className={cn(level > 0 && "ml-3 border-l border-line pl-1")}>
      {(children.get(parent) ?? []).map((queue) => {
        const kids = children.get(queue.id);
        const isOpen = expanded.has(queue.id);
        return (
          <li key={queue.id}>
            <div className="flex items-center">
              {kids ? (
                <button
                  type="button"
                  onClick={() => toggle(queue.id)}
                  aria-expanded={isOpen}
                  aria-label={
                    isOpen
                      ? t("nav.collapseQueue", { name: queue.name })
                      : t("nav.expandQueue", { name: queue.name })
                  }
                  className="grid size-6 shrink-0 place-items-center rounded text-ink-subtle hover:bg-sunken hover:text-ink"
                >
                  <ChevronRight
                    className={cn("size-3.5 transition-transform", isOpen && "rotate-90")}
                    aria-hidden="true"
                  />
                </button>
              ) : (
                <span className="size-6 shrink-0" aria-hidden="true" />
              )}
              <Link
                to={`/tickets?queue=${queue.id}`}
                onClick={onNavigate}
                aria-current={active === queue.id ? "page" : undefined}
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-2 rounded-control px-1.5 py-1 text-[0.8125rem] text-ink-muted",
                  "hover:bg-sunken hover:text-ink",
                  active === queue.id &&
                    "bg-primary-soft font-medium text-primary hover:bg-primary-soft hover:text-primary",
                  !queue.is_valid && "italic",
                )}
                title={queue.full_name}
              >
                <span className="min-w-0 flex-1 truncate">{queue.name}</span>
                <span className="shrink-0 text-xs text-ink-subtle tabular-nums">
                  {formatNumber(queue.tickets)}
                </span>
              </Link>
            </div>
            {kids && isOpen && render(queue.id, level + 1)}
          </li>
        );
      })}
    </ul>
  );

  return <nav aria-label={t("nav.queues")}>{render(null, 0)}</nav>;
}
